import React, { useState } from 'react';

import AiManagementFilePreview from 'components/atoms/ai_management_file_preview';
import { biddingFileHref } from 'services/actions/ai_management_files';
import { formatDate, isExpired } from 'services/ai_management_dates';
import { CANDIDATE_ORIGIN_LABEL } from 'services/ai_management_document_requirements';
import type { AnalystOpinionCandidate } from 'services/ai_management_document_requirements';

interface AiManagementDocumentCandidatesProps {
  candidates: AnalystOpinionCandidate[];
  selectedId: string | null;
  selectable: boolean;
  busyId: string | null;
  onSelect: (candidateId: string) => void;
}

const LEVEL_BADGE: Record<string, { label: string; badge: string }> = {
  alta: { label: 'Alta', badge: 'success' },
  média: { label: 'Média', badge: 'warning' },
  media: { label: 'Média', badge: 'warning' },
  baixa: { label: 'Baixa', badge: 'secondary' },
};

// Tabela "Documentos encontrados no Gerenciar Documentos": o checkbox só seleciona; quem grava é o card.
const AiManagementDocumentCandidates: React.FC<AiManagementDocumentCandidatesProps> = ({
  candidates,
  selectedId,
  selectable,
  busyId,
  onSelect,
}) => {
  const [previewId, setPreviewId] = useState<string | null>(null);

  const renderRow = (candidate: AnalystOpinionCandidate) => {
    const previewing = previewId === candidate.id;
    const selected = selectedId === candidate.id;
    const level = candidate.level ? LEVEL_BADGE[candidate.level] : undefined;
    // Documento vencido não pode ser confirmado (decisão do PO, 02/10/2026).
    const expired = isExpired(candidate.expiresOn);
    const status = expired ? 'Pendente' : candidate.status;

    return (
      <React.Fragment key={candidate.id}>
        <tr className={selected ? 'table-active' : undefined}>
          <td className="align-middle">
            {busyId === candidate.id ? (
              <span
                className="spinner-border spinner-border-sm text-primary"
                role="status"
                aria-label={`Confirmando ${candidate.name}`}
              />
            ) : (
              <input
                type="checkbox"
                checked={selected}
                disabled={!selectable || expired}
                onChange={() => onSelect(candidate.id)}
                aria-label={
                  expired
                    ? `${candidate.name} vencido`
                    : selected
                    ? `${candidate.name} selecionado`
                    : `Selecionar ${candidate.name}`
                }
              />
            )}
          </td>
          <td className="file-name align-middle">
            {candidate.name}
            {candidate.origin && CANDIDATE_ORIGIN_LABEL[candidate.origin] && (
              <small className="d-block text-muted">
                {CANDIDATE_ORIGIN_LABEL[candidate.origin]}
              </small>
            )}
          </td>
          <td className="align-middle">{candidate.group || '—'}</td>
          <td
            className={`align-middle ${status === 'Disponível' ? 'text-success' : 'text-warning'}`}
          >
            {status || '—'}
          </td>
          <td className={`align-middle text-nowrap ${expired ? 'text-danger' : ''}`}>
            {candidate.expiresOn ? formatDate(candidate.expiresOn) : '—'}
          </td>
          <td className="align-middle">
            {level ? <span className={`badge badge-${level.badge}`}>{level.label}</span> : '—'}
          </td>
          <td className="file-actions align-middle text-right text-nowrap">
            {candidate.downloadUrl && (
              <>
                <a
                  className="btn btn-outline-secondary btn-sm mr-1"
                  href={biddingFileHref(candidate.downloadUrl)}
                  download={candidate.name}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Baixar ${candidate.name}`}
                >
                  <i className="material-icons">file_download</i>
                </a>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setPreviewId(previewing ? null : candidate.id)}
                  aria-expanded={previewing}
                  aria-label={previewing ? 'Fechar pré-visualização' : 'Pré-visualizar arquivo'}
                >
                  <i className="material-icons">{previewing ? 'expand_less' : 'expand_more'}</i>
                </button>
              </>
            )}
          </td>
        </tr>
        {previewing && candidate.downloadUrl && (
          <tr className="file-preview-row">
            <td colSpan={7} className="px-3 pb-3 pt-0">
              <AiManagementFilePreview
                name={candidate.name}
                downloadUrl={candidate.downloadUrl}
                detectType
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="mt-4">
      <p className="requirement-section-title mb-1">
        Documentos encontrados no Gerenciar Documentos
      </p>
      <small className="d-block text-danger mb-2">
        * Documentos com a situação Indisponível não serão listados.
      </small>
      <div className="requirement-files card mb-4">
        {candidates.length === 0 ? (
          <div className="text-center px-3 py-4">
            <p className="font-weight-bold mb-1">
              Nenhum documento exigido foi encontrado no Gerenciar Documentos
            </p>
            <p className="text-muted mb-0">
              Não foi possível trazer o documento de forma automática.
            </p>
          </div>
        ) : (
          <table className="table mb-0">
            <thead className="thead-light">
              <tr>
                <th scope="col" aria-label="Selecionar" />
                <th scope="col">Nome do arquivo</th>
                <th scope="col">Grupo</th>
                <th scope="col">Situação</th>
                <th scope="col">Vencimento</th>
                <th scope="col">Compatibilidade</th>
                <th scope="col" aria-label="Ações" />
              </tr>
            </thead>
            <tbody>{candidates.map(renderRow)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AiManagementDocumentCandidates;
